import { useMediaQuery } from '@cascade/react-dom/hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppLayout } from './app/index.ts';
import { CardLayout } from './card/index.ts';
import { LayoutContext } from './Layout.context.ts';
import type { TLayoutProps } from './Layout.types.ts';
import { REGULAR_MEDIA_QUERY } from '#config/index.ts';

export const Layout = (props: TLayoutProps) => {
  const isRegular = useMediaQuery(REGULAR_MEDIA_QUERY);
  const { pathname } = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(isRegular);
  const [asideElement, setAsideElement] = useState<HTMLElement>();

  const isRail = isRegular && !isSidebarOpen;

  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);
  const toggleSidebar = useCallback(
    () => setIsSidebarOpen((isOpen) => !isOpen),
    [],
  );

  const attachAside = useCallback(
    (element: HTMLElement | null) => setAsideElement(element ?? undefined),
    [],
  );

  useEffect(() => {
    setIsSidebarOpen(isRegular);
  }, [isRegular]);

  useEffect(() => {
    if (isRegular) {
      return;
    }

    setIsSidebarOpen(false);
  }, [pathname, isRegular]);

  const context = useMemo(
    () => ({
      isRegular,
      isSidebarOpen,
      isRail,
      asideElement,
      closeSidebar,
      toggleSidebar,
    }),
    [
      isRegular,
      isSidebarOpen,
      isRail,
      asideElement,
      closeSidebar,
      toggleSidebar,
    ],
  );

  return (
    <LayoutContext.Provider value={context}>
      {props.mode === 'app' ? (
        <AppLayout attachAside={attachAside}>{props.children}</AppLayout>
      ) : (
        <CardLayout
          title={props.title}
          subtitle={props.subtitle}
          isLoading={props.isLoading}
          width={props.width}
        >
          {props.children}
        </CardLayout>
      )}
    </LayoutContext.Provider>
  );
};
